import { NEC } from 'constants/globalConstants';
import axiosFormDataInstance from 'utils/axiosFormDataInstance';
import axiosInstance from 'utils/axiosInstance';
const { v4: uuidv4 } = require('uuid');

export type SinisterDocument = {
  numeroPolicy: string;
  numSinistre: string;
  language: string;
  codeSouscripteur: string;
};

export type CADocument = {
  numeroPolicy: string;
  adhesionNumber: string;
  adhesionIdentifier: string;
  language: string;
  codeSouscripteur: string;
  policyIdentifier?: string;
};

const buildQueryParams = (data: SinisterDocument) =>
  `PolicyReference=${data.numeroPolicy}&codeSouscripteur=${
    data.codeSouscripteur
  }&SinisterNumber=${data.numSinistre}&LanguageCode=${
    data.language
  }&CallingChannel=${NEC}&CallerId=${uuidv4()}`;

const buildCAQueryParams = (data: CADocument) => {
  let query = `PolicyReference=${data.numeroPolicy}&codeSouscripteur=${
    data.codeSouscripteur
  }&AdhesionNumber=${data.adhesionNumber}&AdhesionIdentifier=${
    data.adhesionIdentifier
  }&LanguageCode=${data.language}&CallingChannel=${NEC}&CallerId=${uuidv4()}`;
  if (data.policyIdentifier) {
    query = query + `&PolicyIdentifier=${data.policyIdentifier}`;
  }
  return query;
};

export const getSinistreApi = ({ language }: { language: string }) => {
  return axiosInstance.get('/claims', {
    params: { lang: language },
  });
};

export const getSinistreDetailsApi = ({
  language,
  claimNumber,
}: {
  language: string;
  claimNumber: String;
}) => {
  return axiosInstance.get(`/get-claim-details/${claimNumber}`, {
    params: { lang: language },
  });
};

export const getRefundsAMCApi = ({ perPage, page, beneficiary }: any = {}) =>
  axiosInstance.post('/health/amcRefunds', {
    filter: {
      perPage: perPage || 10,
      page: page || 1,
      beneficiary: beneficiary || '',
    },
  });

export function getDetailRefundAMCApi(payload: any) {
  return axiosInstance.get(
    `/health/amcDetailRefund?numDossier=${payload.payload.numeroApplication.trim()}&numPolice=${payload.payload.numeroPolicy.trim()}&callerId=${
      buildSource().CallerId
    }&callingChannel=${buildSource().CallingChannelCode}`,
  );
}

export function checkExistMissingDocumentsRequestApi(payload: any) {
  return axiosInstance.get(
    `/health/sinisterDemands/SinisterDemands/ExistByCriteria?${buildCheckQueryParams(
      payload,
    )}`,
  );
}

export const submitCIApi = (payload: any) => {
  return axiosFormDataInstance.post(
    '/health/sinisterDemands/SinisterDemands/Submit',
    buildRequestData(payload),
  );
};

export const downloadDecompte = (data: any) =>
  axiosInstance.get(
    `/health/sinisters/Sinisters/DownloadRefundDocumentByCriteria?${buildQueryParams(
      data,
    )}`,
  );

export const downloadCA = (data: any) =>
  axiosInstance.get(
    `/health/adherents/Adherents/DownloadMembershipDocumentByCriteria?${buildCAQueryParams(
      data,
    )}`,
  );

export const downloadConvocationApi = (data: any) =>
  axiosInstance.get(
    `/health/sinisters/Sinisters/DownloadMedicalVisitDocumentByCriteria?${buildQueryParams(
      data,
    )}`,
  );

export const downloadAccordApi = (data: any) =>
  axiosInstance.get(
    `/health/sinisters/Sinisters/DownloadAgreementDocumentByCriteria?${buildQueryParams(
      data,
    )}`,
  );

export const downloadInfoComplementaryApi = (data: any) =>
  axiosInstance.get(
    `/health/sinisters/Sinisters/DownloadInformationComplementDocumentByCriteria?${buildQueryParams(
      data,
    )}`,
  );

export function buildCheckQueryParams(data: any) {
  const { CallerId, CallingChannelCode } = buildSource();
  return `SinisterNumber=${
    data.sinisterNumber
  }&SinisterDemandTypeCode=${SinisterDemandTypeCode}&PolicyId=${
    data.policyId
  }&LanguageCode=${
    data.LanguageCode
  }&SinisterDemandStatusCode=${'Submitted'}&CallingChannelCode=${CallingChannelCode}&CallerId=${CallerId}`;
}

export const SinisterDemandTypeCode = 'SinisterInformationComplement';

export const buildSource = () => ({
  CallerId: uuidv4(),
  CallingChannelCode: 'WebClientsFrontOffice',
  ClientUserId: 1,
});

export const buildRequestData = ({ Documents = [], ...payload }: any) => {
  const data: any = buildFormData({
    ...buildSource(),
    ...payload,
  });
  Documents.forEach((doc: any) => {
    const byteCharacters = atob(doc.data?.split(',')[1]);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }

    const blob = new Blob([new Uint8Array(byteArrays)], {
      type: 'application/octet-stream',
    });

    data.append('Documents', blob, doc.name);
  });

  return data;
};

export const buildFormData = (payload: any) => {
  const result = new FormData();
  Object.entries(payload).forEach(([name, data]: any) => {
    result.append(name, data);
  });
  return result;
};
